'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import { buildHydratedAiAnalysisState } from '@/lib/aiAnalysisHydration';
import { getPersistableEntities } from '@/lib/entityUtils';
import { selectKnowledgeStateForPersistence } from '@/lib/incident-report/knowledgePersistence';
import { selectMapLayerConfigForPersistence } from '@/lib/maps/mapLayerConfigPersistence';
import {
  applyActionStateProjection,
  stripActionState,
  type JsonRecord,
} from '@/lib/incident-report/aiAnalysisPersistence';
import { AiAnalysisState, SuggestedAction } from '@/store/slices/aiAnalysisTypes';
import {
  hydrateAiAnalysisState,
  hydrateCanonicalActionProjection,
  setAnalysisHydrating,
} from '@/store/slices/aiAnalysisSlice';
import { RootState } from '@/store/store';

/**
 * Operator-authoritative fields on a SuggestedAction. These represent
 * decisions made by the officer or AI-detected lifecycle state captured
 * locally; they are NOT sent on the LiveKit `ai_analysis_json` push, so
 * if we let a LiveKit refresh wholesale-replace an action by id we will
 * silently drop them. On rejoin we must overlay these fields back onto
 * the server-pushed action body when merging DB state with live state.
 */
const OPERATOR_ACTION_FIELDS: Array<keyof SuggestedAction> = [
  'answerStatus',
  'answered',
  'answeredAt',
  'answer',
  'partialAnswer',
  'questionParts',
  'answeredParts',
  'unansweredParts',
  'answerReasoning',
  'answeredPartsLegacy',
  'correctedAt',
  'tierMatched',
  'userDeprioritized',
  'userDeprioritizedAt',
  'note',
  'officerAddressed',
  'officerAddressedAt',
  'officerAddressedPartial',
  'officerQuote',
  'officerAddressedReason',
  'spokenAt',
  'speakAttempts',
  'answerSource',
  'confidence',
];

/**
 * Merge an operator-flag-bearing action from the DB onto the
 * server-pushed action body from Redux. Server fields win for the
 * body; operator fields from DB win when Redux doesn't have them
 * (i.e. when the LiveKit push has reset them to undefined).
 */
function mergeActionPreservingOperatorFlags(
  liveAction: SuggestedAction,
  dbAction: SuggestedAction | undefined,
): SuggestedAction {
  if (!dbAction) return liveAction;
  const merged: SuggestedAction = { ...liveAction };
  for (const field of OPERATOR_ACTION_FIELDS) {
    const liveVal = liveAction[field];
    const dbVal = dbAction[field];
    if ((liveVal === undefined || liveVal === null) && dbVal !== undefined && dbVal !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[field] = dbVal;
    }
  }
  return merged;
}

/**
 * Merge live (LiveKit-pushed) action arrays with DB-loaded action arrays.
 * For each live action, find the matching DB entry by id and overlay
 * operator-only flags. Any DB actions not present in the live array are
 * preserved (they may be answered/discarded items the server is no
 * longer pushing in the active subset).
 */
function mergeActionArrays(
  liveActions: SuggestedAction[] | undefined,
  dbActions: SuggestedAction[] | undefined,
): SuggestedAction[] {
  const live = Array.isArray(liveActions) ? liveActions : [];
  const db = Array.isArray(dbActions) ? dbActions : [];
  if (live.length === 0) return db;
  if (db.length === 0) return live;

  const dbById = new Map<string, SuggestedAction>();
  for (const a of db) {
    if (a?.id) dbById.set(a.id, a);
  }

  const seen = new Set<string>();
  const merged: SuggestedAction[] = live.map((a) => {
    if (a?.id) seen.add(a.id);
    return mergeActionPreservingOperatorFlags(a, a?.id ? dbById.get(a.id) : undefined);
  });

  // Append DB-only actions (e.g. answered/discarded items the server
  // no longer pushes in the active subset).
  for (const a of db) {
    if (a?.id && !seen.has(a.id)) {
      merged.push(a);
    }
  }

  return merged;
}

/**
 * AI Analysis Real-Time Sync Hook
 *
 * Provides tiered real-time Supabase sync for AI analysis state.
 * Different data has different sync priorities:
 *
 * TIER 1 (immediate, 200ms): Critical user-modified data
 *   - entities (extracted caller, location, etc.)
 *   - pinnedItems (user's manual selections)
 *
 * TIER 2 (2s debounce): Frequently changing AI data
 *   - suggestedActions (AI suggestions)
 *   - activityFeed (timeline events)
 *
 * TIER 3 (5s debounce): Less critical tracking data
 *   - aiQueries, aiSearches (conversation history)
 *   - pendingQuickNotes (draft notes)
 *
 * Supabase is the only durable source of truth for aiAnalysis.
 * This hook writes there directly and rehydrates from there on mount.
 */

// Sync debounce times for different tiers
const TIER1_DEBOUNCE_MS = 200;   // Critical data - near-immediate
const TIER2_DEBOUNCE_MS = 2000;  // AI suggestions - 2s
const TIER3_DEBOUNCE_MS = 5000;  // Queries/searches - 5s

interface UseAiAnalysisSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
}

interface AiAnalysisSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
  tier1LastSync: number | null;
  tier2LastSync: number | null;
  tier3LastSync: number | null;
}

interface AiAnalysisSyncResult extends AiAnalysisSyncState {
  /** Force re-load AI analysis from Supabase (bypasses hasLoadedRef guard). Use on reconnect. */
  reloadFromSupabase: () => Promise<void>;
}

/**
 * Compute hash for tier data to detect changes
 */
function computeTier1Hash(state: AiAnalysisState): string {
  return JSON.stringify({
    entities: state.entities,
    pinnedItems: state.pinnedItems?.map(p => ({ id: p.id, at: p.pinnedAt })),
  });
}

function computeTier2Hash(state: AiAnalysisState): string {
  return JSON.stringify({
    suggestedActions: state.suggestedActions?.map(a => ({
      id: a.id,
      status: a.answerStatus,
      answered: a.answered,
    })),
    allSuggestedActionsCount: state.allSuggestedActions?.length,
    allSuggestedActionsAnswered: state.allSuggestedActions?.filter(a => a.answered)?.length,
    activityFeed: state.activityFeed?.events?.length,
  });
}

function computeTier3Hash(state: AiAnalysisState): string {
  return JSON.stringify({
    aiQueries: state.aiQueries?.length,
    aiSearches: state.aiSearches?.length,
    pendingQuickNotes: state.pendingQuickNotes?.length,
    // A new Knowledge result must reach full_state during the call too.
    relatedDocuments: (state.relatedDocumentsSnapshot || []).map((doc) => doc?.documentId ?? doc?.id ?? null),
    relatedDocumentsOverallSummary: state.relatedDocumentsOverallSummary ?? null,
    mapLayerConfig: state.mapLayerConfig ?? null,
  });
}

function selectActionsForCanonicalIngest(analysis: AiAnalysisState): SuggestedAction[] {
  return analysis.allSuggestedActions?.length
    ? analysis.allSuggestedActions
    : [...(analysis.suggestedActions || []), ...(analysis.queuedActions || [])];
}

function buildAiAnalysisFullState(
  analysis: AiAnalysisState,
  entitiesForPersistence: ReturnType<typeof getPersistableEntities>,
): JsonRecord {
  return {
    suggestedActions: analysis.suggestedActions,
    allSuggestedActions: analysis.allSuggestedActions,
    queuedActions: analysis.queuedActions,
    discardedSuggestions: analysis.discardedSuggestions,
    resolvedSuggestions: analysis.resolvedSuggestions,
    removedSuggestions: analysis.removedSuggestions,
    partialSuggestions: analysis.partialSuggestions,
    answeredPrompts: analysis.answeredPrompts,
    partialPrompts: analysis.partialPrompts,
    detectedPrompts: analysis.detectedPrompts,
    lockedPrompts: analysis.lockedPrompts,
    pinnedItems: analysis.pinnedItems,
    pendingQuickNotes: analysis.pendingQuickNotes,
    aiQueries: analysis.aiQueries,
    aiSearches: analysis.aiSearches,
    ...(entitiesForPersistence ? { entities: entitiesForPersistence } : {}),
    activityFeed: analysis.activityFeed,
    // full_state is upserted whole, so a key missing here is ERASED on every
    // sync — this is how the Knowledge panel's state never reached the report.
    ...selectKnowledgeStateForPersistence(analysis),
    ...selectMapLayerConfigForPersistence(analysis),
  };
}

function applyCanonicalActionProjection(
  fullState: JsonRecord,
  projection: Partial<AiAnalysisState> | null,
): JsonRecord {
  if (!projection) return fullState;
  return applyActionStateProjection(stripActionState(fullState), projection as JsonRecord);
}

function selectProjectedActionArchive(projection: Partial<AiAnalysisState> | null): unknown[] {
  if (!projection) return [];
  const projected = projection as Record<string, unknown>;
  if (Array.isArray(projected.allSuggestedActions)) return projected.allSuggestedActions;
  if (Array.isArray(projected.suggestedActions)) return projected.suggestedActions;
  return [];
}

async function loadCanonicalActionProjection(
  callId: string,
): Promise<Partial<AiAnalysisState> | null> {
  try {
    const response = await fetch(`/api/call-actions?callId=${encodeURIComponent(callId)}`);
    if (!response.ok) return null;
    const snapshot = await response.json().catch(() => null);
    const projection = snapshot?.projection;
    if (!projection || typeof projection !== 'object') return null;
    if (snapshot?.source !== 'canonical') return null;
    return projection as Partial<AiAnalysisState>;
  } catch (error) {
    logger.debug('useAiAnalysisSync', 'Canonical action hydration unavailable', { error, callId });
    return null;
  }
}

function mergeCanonicalActionProjectionForHydration(
  legacyLoadedState: Partial<AiAnalysisState>,
  canonicalState: Partial<AiAnalysisState> | null,
): Partial<AiAnalysisState> {
  if (!canonicalState) {
    return legacyLoadedState;
  }
  const canonicalForHydration = { ...canonicalState };
  delete (canonicalForHydration as { canonicalRevision?: number }).canonicalRevision;
  delete (canonicalForHydration as { canonicalStateRevision?: number }).canonicalStateRevision;

  return {
    ...(stripActionState(legacyLoadedState as JsonRecord) as Partial<AiAnalysisState>),
    ...canonicalForHydration,
  };
}

async function ingestActionsToCanonical(
  callId: string,
  analysis: AiAnalysisState,
): Promise<Partial<AiAnalysisState> | null> {
  const actions = selectActionsForCanonicalIngest(analysis);

  if (!callId || actions.length === 0) return null;

  try {
    const response = await fetch('/api/call-actions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operation: 'ingest_generated',
        callId,
        roomName: callId,
        actions,
        source: 'ui-sync',
        mutationId: `ui-sync:${callId}:${Date.now()}`,
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      logger.debug('useAiAnalysisSync', 'Canonical ingest skipped', {
        callId,
        status: response.status,
        error: body?.error,
      });
      return null;
    }

    const snapshot = await response.json().catch(() => null);
    const projection = snapshot?.projection;
    if (snapshot?.source === 'canonical' && projection && typeof projection === 'object') {
      return projection as Partial<AiAnalysisState>;
    }
  } catch (error) {
    logger.debug('useAiAnalysisSync', 'Canonical ingest unavailable', { error, callId });
  }
  return null;
}

/**
 * Hook to sync AI analysis to Supabase with tiered debouncing
 */
export function useAiAnalysisSync(options: UseAiAnalysisSyncOptions): AiAnalysisSyncResult {
  const { callId, enabled = true } = options;

  const dispatch = useDispatch();
  const store = useStore();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  const aiAnalysis = useSelector((state: RootState) => state.aiAnalysis);

  // Refs for debounce timeouts
  const tier1TimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tier2TimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tier3TimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for hash tracking (avoid redundant syncs)
  const tier1HashRef = useRef<string | null>(null);
  const tier2HashRef = useRef<string | null>(null);
  const tier3HashRef = useRef<string | null>(null);

  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const canonicalProjectionRef = useRef<Partial<AiAnalysisState> | null>(null);

  const stateRef = useReactiveRef<AiAnalysisSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
    tier1LastSync: null,
    tier2LastSync: null,
    tier3LastSync: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useAiAnalysisSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync AI analysis to Supabase (full sync)
   */
  const syncToSupabase = useCallback(async (
    analysis: AiAnalysisState,
    tier: 'tier1' | 'tier2' | 'tier3' | 'full'
  ) => {
    if (!enabled || !callId) return;

    // Check if hash changed for this tier
    const currentTier1Hash = computeTier1Hash(analysis);
    const currentTier2Hash = computeTier2Hash(analysis);
    const currentTier3Hash = computeTier3Hash(analysis);

    let needsSync = false;
    if (tier === 'full') {
      needsSync = true;
    } else if (tier === 'tier1' && currentTier1Hash !== tier1HashRef.current) {
      needsSync = true;
    } else if (tier === 'tier2' && currentTier2Hash !== tier2HashRef.current) {
      needsSync = true;
    } else if (tier === 'tier3' && currentTier3Hash !== tier3HashRef.current) {
      needsSync = true;
    }

    if (!needsSync) {
      logger.debug('useAiAnalysisSync', `Skipping ${tier} sync - no changes`);
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      const entitiesForPersistence = getPersistableEntities(analysis.entities);
      let canonicalProjection: Partial<AiAnalysisState> | null = null;

      if (tier === 'tier2' || tier === 'full') {
        canonicalProjection = await ingestActionsToCanonical(callId, analysis);
      }
      if (!canonicalProjection) {
        canonicalProjection = await loadCanonicalActionProjection(callId);
      }
      if (canonicalProjection) {
        canonicalProjectionRef.current = canonicalProjection;
      }

      // Build the DB payload
      const dbPayload: Record<string, unknown> = {
        call_id: dbCallId,
      };

      // Include data based on tier (all tiers update full_state too)
      // GUARD: Skip writing empty entities/actions/activityFeed to avoid
      // overwriting backend-written data with empty Redux state.
      if (tier === 'tier1' || tier === 'full') {
        if (entitiesForPersistence) {
          dbPayload.entities = entitiesForPersistence;
        }
        // pinnedItems are user-driven actions — always write
        dbPayload.pinned_items = analysis.pinnedItems;
      }

      if (tier === 'tier2' || tier === 'full') {
        const projectedActions = selectProjectedActionArchive(canonicalProjection);
        if (canonicalProjection) {
          dbPayload.suggested_actions = projectedActions;
        } else {
          // Legacy fallback for calls that have not been backfilled yet. Once
          // canonical rows exist, this column is written from canonical rows
          // only so Redux cannot clobber accepted lifecycle state.
          const actions = selectActionsForCanonicalIngest(analysis);
          if (actions.length > 0) {
            dbPayload.suggested_actions = actions;
          }
        }
        // Only write activity feed if it has events
        if (analysis.activityFeed?.events && analysis.activityFeed.events.length > 0) {
          dbPayload.activity_feed = analysis.activityFeed;
        }
      }

      // Always include full_state for comprehensive backup
      dbPayload.full_state = applyCanonicalActionProjection(
        buildAiAnalysisFullState(analysis, entitiesForPersistence),
        canonicalProjection,
      );

      // Upsert AI analysis
      const { error } = await supabase
        .from('ai_analysis')
        .upsert(dbPayload, {
          onConflict: 'call_id',
        });

      if (error) {
        logger.error('useAiAnalysisSync', 'Supabase upsert failed', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          callId: dbCallId,
        });
        throw error;
      }

      if (tier === 'tier1' || tier === 'full') {
        tier1HashRef.current = currentTier1Hash;
        stateRef.current.tier1LastSync = Date.now();
      }
      if (tier === 'tier2' || tier === 'full') {
        tier2HashRef.current = currentTier2Hash;
        stateRef.current.tier2LastSync = Date.now();
      }
      if (tier === 'tier3' || tier === 'full') {
        tier3HashRef.current = currentTier3Hash;
        stateRef.current.tier3LastSync = Date.now();
      }

      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useAiAnalysisSync', `Synced ${tier} to Supabase`, {
        callId,
        actionsCount: analysis.suggestedActions?.length,
      });
    } catch (err: unknown) {
      logger.error('useAiAnalysisSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  /**
   * Load AI analysis from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async () => {
    if (!enabled || !callId || hasLoadedRef.current) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    hasLoadedRef.current = true;
    // #1701: entity tabs show a loading state, not "no data", while this runs.
    dispatch(setAnalysisHydrating(true));

    try {
      // Load from Supabase only. aiAnalysis has a single durable source of truth.
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      const canonicalState = await loadCanonicalActionProjection(callId);
      if (canonicalState) {
        canonicalProjectionRef.current = canonicalState;
      }

      const { data: analysisData, error } = await supabase
        .from('ai_analysis')
        .select('*')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if ((canonicalState || analysisData) && mountedRef.current) {
        const legacyLoadedState = buildHydratedAiAnalysisState(analysisData);
        const loadedState = mergeCanonicalActionProjectionForHydration(
          legacyLoadedState,
          canonicalState,
        );

        // REJOIN MERGE (#305): The LiveKit `ai_analysis_json` push that
        // fires immediately on rejoin re-establishes server-authoritative
        // entities + suggestedActions in Redux, but it does NOT include
        // operator-authoritative state: pinnedItems, answeredPrompts,
        // partialPrompts, queuedActions, discarded/removed/partial
        // suggestions, pendingQuickNotes, locked/detected prompts, or
        // per-action operator flags (answerStatus, note, spokenAt, etc.).
        //
        // The previous guard skipped DB hydration entirely whenever any
        // live entities or actions were present in Redux — silently
        // dropping all of the above the moment the backend re-pushed.
        //
        // Instead: always restore the operator-authoritative slice from
        // the DB. If LiveKit has already pushed live actions, merge them
        // by id, overlaying operator flags from DB onto the live action
        // body. If Redux is empty (no live push yet), fall back to the
        // DB-loaded state outright.
        const currentState = (store.getState() as RootState).aiAnalysis;
        const hasLiveEntities = (currentState.entities?.people?.length || 0) +
          (currentState.entities?.locations?.length || 0) +
          (currentState.entities?.objects?.length || 0) +
          (currentState.entities?.organizations?.length || 0) +
          (currentState.entities?.activity?.length || 0) > 0;
        const hasLiveActions = currentState.suggestedActions.length > 0 ||
          currentState.allSuggestedActions.length > 0;

        // Build the merged state we will dispatch.
        // - Server-authoritative fields (entities, action body, activityFeed):
        //     prefer LIVE state if present, else DB.
        // - Operator-authoritative fields (pins, answered/partial sets,
        //     queued/discarded/removed/partial suggestions, pendingQuickNotes,
        //     locked/detected prompts, per-action operator flags):
        //     ALWAYS prefer DB — those are what the rejoined client has lost.
        const mergedState: Partial<AiAnalysisState> = { ...loadedState };

        if (hasLiveEntities) {
          mergedState.entities = currentState.entities;
        }

        if (hasLiveActions && !canonicalState) {
          // Merge by id so operator flags survive the LiveKit-side reset.
          mergedState.suggestedActions = mergeActionArrays(
            currentState.suggestedActions,
            loadedState.suggestedActions as SuggestedAction[] | undefined,
          );
          mergedState.allSuggestedActions = mergeActionArrays(
            currentState.allSuggestedActions,
            loadedState.allSuggestedActions as SuggestedAction[] | undefined,
          );
        }

        // Activity feed: prefer live if it has events, else DB.
        if (currentState.activityFeed?.events && currentState.activityFeed.events.length > 0) {
          mergedState.activityFeed = currentState.activityFeed;
        }

        // Top-level operator-authoritative arrays — DB is the source of
        // truth for these on rejoin (LiveKit push doesn't include them).
        // buildHydratedAiAnalysisState already populated them from full_state.
        // Nothing to do; they're already in mergedState.

        dispatch(hydrateAiAnalysisState(mergedState));
        if (canonicalState) {
          dispatch(hydrateCanonicalActionProjection(canonicalState));
        }
        const hashState = mergedState;

        logger.info('useAiAnalysisSync', 'Hydrated AI analysis from Supabase (merged with live state)', {
          hadLiveEntities: hasLiveEntities,
          hadLiveActions: hasLiveActions,
          liveActionCount: currentState.suggestedActions.length,
          dbActionCount: loadedState.suggestedActions?.length || 0,
          mergedActionCount: hashState.suggestedActions?.length || 0,
          source: canonicalState ? 'canonical' : 'ai_analysis',
          hasPinnedItems: !!loadedState.pinnedItems?.length,
          answeredPromptsCount: loadedState.answeredPrompts?.length || 0,
          partialPromptsCount: loadedState.partialPrompts?.length || 0,
        });

        // Update hashes based on the MERGED state (what's now in Redux)
        // to prevent immediate re-sync. Cast through unknown because
        // hashes only inspect a few fields.
        tier1HashRef.current = computeTier1Hash(hashState as AiAnalysisState);
        tier2HashRef.current = computeTier2Hash(hashState as AiAnalysisState);
        tier3HashRef.current = computeTier3Hash(hashState as AiAnalysisState);
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useAiAnalysisSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    } finally {
      dispatch(setAnalysisHydrating(false));
    }
  // NOTE: aiAnalysis intentionally excluded - including it causes infinite loop
  // because this function dispatches hydrateAiAnalysisState which updates aiAnalysis
  }, [enabled, callId, supabase, dispatch]);

  /**
   * Force re-load AI analysis from Supabase, bypassing the hasLoadedRef guard.
   * Called on LiveKit reconnection to restore any data lost during disconnect.
   */
  const reloadFromSupabase = useCallback(async () => {
    if (!enabled || !callId) return;

    logger.info('useAiAnalysisSync', 'Force reloading AI analysis from Supabase (reconnect)', { callId });

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!callData?.id) {
        logger.debug('useAiAnalysisSync', 'No call data found during reload', { callId });
        return;
      }

      dbCallIdRef.current = callData.id;

      const canonicalState = await loadCanonicalActionProjection(callId);
      if (canonicalState) {
        canonicalProjectionRef.current = canonicalState;
      }

      const { data: analysisData, error } = await supabase
        .from('ai_analysis')
        .select('*')
        .eq('call_id', callData.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if ((canonicalState || analysisData) && mountedRef.current) {
        const legacyLoadedState = buildHydratedAiAnalysisState(analysisData);
        const loadedState = mergeCanonicalActionProjectionForHydration(
          legacyLoadedState,
          canonicalState,
        );

        // RECONNECT merge policy (Codex Risk #7) — NOTE this is the INVERSE of
        // loadFromSupabase's mount policy for operator-authoritative state.
        // On a fresh MOUNT, Redux holds only initialState, so DB is the source
        // of truth for operator arrays. On RECONNECT the client stayed alive
        // through the transport blip, so Redux holds the LIVE operator state —
        // possibly fresher than DB, because the tiered sync debounce (200ms–5s)
        // may not have flushed the operator's most recent pins / notes /
        // dispositions before the disconnect. Taking those wholesale from DB
        // (the previous behavior) silently reverted un-flushed operator intent.
        // So: prefer Redux for operator-authoritative fields; keep DB/merge for
        // server-authoritative fields (entities, action bodies, activityFeed).
        const currentState = (store.getState() as RootState).aiAnalysis;
        const hasLiveEntities = (currentState.entities?.people?.length || 0) +
          (currentState.entities?.locations?.length || 0) +
          (currentState.entities?.objects?.length || 0) +
          (currentState.entities?.organizations?.length || 0) +
          (currentState.entities?.activity?.length || 0) > 0;
        const hasLiveActions = currentState.suggestedActions.length > 0 ||
          currentState.allSuggestedActions.length > 0;

        const mergedState: Partial<AiAnalysisState> = { ...loadedState };

        // Pure operator-authoritative fields — not carried by the reconnect
        // re-push and not part of the canonical action projection. Prefer Redux
        // WHOLESALE (even when empty: post-mount an empty array is a deliberate
        // operator clear, e.g. unpin-to-empty, not "not yet loaded"). Note: this
        // intentionally lets the post-merge tier-hash recompute suppress the
        // pending DB flush — Redux stays authoritative on every reconnect, so
        // there is no in-session loss; DB reconverges on the next operator edit.
        mergedState.pinnedItems = currentState.pinnedItems;
        mergedState.pendingQuickNotes = currentState.pendingQuickNotes;

        if (hasLiveEntities) {
          mergedState.entities = currentState.entities;
        }
        if (hasLiveActions && !canonicalState) {
          mergedState.suggestedActions = mergeActionArrays(
            currentState.suggestedActions,
            loadedState.suggestedActions as SuggestedAction[] | undefined,
          );
          mergedState.allSuggestedActions = mergeActionArrays(
            currentState.allSuggestedActions,
            loadedState.allSuggestedActions as SuggestedAction[] | undefined,
          );
          // Operator action-dispositions: in the LEGACY (non-canonical) path the
          // hydrateAiAnalysisState reducer takes these straight from the DB
          // payload, so they clobber un-flushed operator dispositions on
          // reconnect just like pinnedItems. Prefer Redux here too. (In the
          // canonical path, hydrateCanonicalActionProjection governs them via
          // its monotonic revision gate, so we do NOT override them there.)
          mergedState.discardedSuggestions = currentState.discardedSuggestions;
          mergedState.removedSuggestions = currentState.removedSuggestions;
          mergedState.resolvedSuggestions = currentState.resolvedSuggestions;
          mergedState.partialSuggestions = currentState.partialSuggestions;
        }
        if (currentState.activityFeed?.events && currentState.activityFeed.events.length > 0) {
          mergedState.activityFeed = currentState.activityFeed;
        }

        if (Object.keys(mergedState).length > 0 || !canonicalState) {
          dispatch(hydrateAiAnalysisState(mergedState));
        }
        if (canonicalState) {
          dispatch(hydrateCanonicalActionProjection(canonicalState));
        }

        tier1HashRef.current = computeTier1Hash(mergedState as AiAnalysisState);
        tier2HashRef.current = computeTier2Hash(mergedState as AiAnalysisState);
        tier3HashRef.current = computeTier3Hash(mergedState as AiAnalysisState);

        logger.info('useAiAnalysisSync', 'Reloaded AI analysis from Supabase on reconnect (merged with live state)', {
          hadLiveEntities: hasLiveEntities,
          hadLiveActions: hasLiveActions,
          liveActionCount: currentState.suggestedActions.length,
          dbActionCount: loadedState.suggestedActions?.length || 0,
          mergedActionCount: mergedState.suggestedActions?.length || 0,
          source: canonicalState ? 'canonical' : 'ai_analysis',
          // Operator-authoritative fields are preferred from Redux on reconnect
          // (Risk #7) — log the value actually dispatched (mergedState), not DB.
          preservedPinnedItems: mergedState.pinnedItems?.length || 0,
          preservedPendingQuickNotes: mergedState.pendingQuickNotes?.length || 0,
          dbAnsweredPromptsCount: loadedState.answeredPrompts?.length || 0,
          dbPartialPromptsCount: loadedState.partialPrompts?.length || 0,
        });
      }
    } catch (err: unknown) {
      logger.error('useAiAnalysisSync', 'Failed to reload from Supabase on reconnect', { error: err });
    }
  }, [enabled, callId, supabase, dispatch]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // TIER 1: Entities and pinned items (critical, near-immediate)
  useEffect(() => {
    if (!enabled || !callId) return;

    const currentHash = computeTier1Hash(aiAnalysis);
    if (currentHash === tier1HashRef.current) return;

    if (tier1TimeoutRef.current) {
      clearTimeout(tier1TimeoutRef.current);
    }

    tier1TimeoutRef.current = setTimeout(() => {
      syncToSupabase(aiAnalysis, 'tier1');
    }, TIER1_DEBOUNCE_MS);
  }, [
    enabled,
    callId,
    aiAnalysis.entities,
    aiAnalysis.pinnedItems,
    syncToSupabase,
    aiAnalysis,
  ]);

  // TIER 2: Suggested actions and activity feed (2s debounce)
  useEffect(() => {
    if (!enabled || !callId) return;

    const currentHash = computeTier2Hash(aiAnalysis);
    if (currentHash === tier2HashRef.current) return;

    if (tier2TimeoutRef.current) {
      clearTimeout(tier2TimeoutRef.current);
    }

    tier2TimeoutRef.current = setTimeout(() => {
      syncToSupabase(aiAnalysis, 'tier2');
    }, TIER2_DEBOUNCE_MS);
  }, [
    enabled,
    callId,
    aiAnalysis.suggestedActions,
    aiAnalysis.activityFeed,
    syncToSupabase,
    aiAnalysis,
  ]);

  // TIER 3: Queries and searches (5s debounce)
  useEffect(() => {
    if (!enabled || !callId) return;

    const currentHash = computeTier3Hash(aiAnalysis);
    if (currentHash === tier3HashRef.current) return;

    if (tier3TimeoutRef.current) {
      clearTimeout(tier3TimeoutRef.current);
    }

    tier3TimeoutRef.current = setTimeout(() => {
      syncToSupabase(aiAnalysis, 'tier3');
    }, TIER3_DEBOUNCE_MS);
  }, [
    enabled,
    callId,
    aiAnalysis.aiQueries,
    aiAnalysis.aiSearches,
    aiAnalysis.pendingQuickNotes,
    syncToSupabase,
    aiAnalysis,
  ]);

  // Emergency flush: do not write directly to Supabase REST from beforeunload.
  // The normal sync path applies canonical projection before persistence; a raw
  // anon-key keepalive write can clobber server-owned fields on navigation.
  useEffect(() => {
    if (!enabled || !callId) return;

    const handleBeforeUnload = () => {
      // Cancel pending debounce timers — we're flushing everything now
      if (tier1TimeoutRef.current) clearTimeout(tier1TimeoutRef.current);
      if (tier2TimeoutRef.current) clearTimeout(tier2TimeoutRef.current);
      if (tier3TimeoutRef.current) clearTimeout(tier3TimeoutRef.current);
      void syncToSupabase(aiAnalysis, 'full');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, callId, aiAnalysis, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (tier1TimeoutRef.current) clearTimeout(tier1TimeoutRef.current);
      if (tier2TimeoutRef.current) clearTimeout(tier2TimeoutRef.current);
      if (tier3TimeoutRef.current) clearTimeout(tier3TimeoutRef.current);
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

/**
 * Hook to hydrate AI analysis from storage
 */
export function useAiAnalysisHydration(callId: string): {
  hydrate: () => Promise<Partial<AiAnalysisState> | null>;
  isHydrated: boolean;
} {
  const supabase = createClient();
  const hydratedRef = useRef(false);

  const hydrate = useCallback(async (): Promise<Partial<AiAnalysisState> | null> => {
    if (hydratedRef.current || !callId) return null;

    try {
      const { data: callData } = await supabase
        .from('calls')
        .select('id')
        .eq('call_id', callId)
        .maybeSingle();

      let result: Partial<AiAnalysisState> | null = null;
      if (callData?.id) {
        const { data: analysisData } = await supabase
          .from('ai_analysis')
          .select('*')
          .eq('call_id', callData.id)
          .maybeSingle();

        if (analysisData?.full_state) {
          result = analysisData.full_state as Partial<AiAnalysisState>;
          logger.info('useAiAnalysisHydration', 'Hydrated from Supabase');
        }
      }

      hydratedRef.current = true;
      return result;
    } catch (err: unknown) {
      logger.error('useAiAnalysisHydration', 'Hydration failed', { error: err });
      return null;
    }
  }, [callId, supabase]);

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useAiAnalysisSync;
